/**
 * Browser-safe copy of the custom Liquid filters in
 * `packages/backend/sdk/lib/liquid-template/functions.ts`.
 *
 * The SDK source imports its error classes from `apollo-server-express`, which
 * pulls Express/Node into any bundle that touches it — so it cannot be imported
 * by the frontend. This copy is byte-identical in filter behavior but throws
 * plain `Error` subclasses instead, making `commonFunctions` registerable on the
 * client-side LiquidJS engines used by the v9→v10 migration preview.
 *
 * Drift is guarded by `functions.test.ts`, which renders every filter through
 * both this copy and the SDK original and asserts identical output (and
 * identical error messages on the throwing paths). If you change a filter in the
 * SDK, change it here too — the parity test will fail until you do.
 *
 * These two error classes mirror the apollo ones used by the SDK only insofar as
 * the migration tool needs: a thrown `.message` the UI can surface. They are not
 * GraphQL errors and carry no apollo semantics.
 */
import atob from "atob";
import btoa from "btoa";
import crypto from "crypto-js";
import * as d3 from "d3-time-format";

class UserInputError extends Error {}
class ValidationError extends Error {}

/**
 * Keep in sync with the SDK copy: null/undefined hash subjects must degrade to
 * "" (not throw, and not hash as empty-string digests). See SDK `isNil` note.
 */
function isNil(val: unknown): val is null | undefined {
  return val === null || val === undefined;
}

export function sha256(val: string): string {
  if (isNil(val)) return "";
  return crypto.SHA256(val).toString();
}

export function sha512(val: string): string {
  if (isNil(val)) return "";
  return crypto.SHA512(val).toString();
}

export function md5(val: string): string {
  if (isNil(val)) return "";
  return crypto.MD5(val).toString();
}

function parse(val: string | Record<string, unknown>): Record<string, unknown> {
  if (typeof val === "object") {
    return val;
  }
  return JSON.parse(val);
}

// Object.fromEntries defines own properties; assigning them would let a
// `__proto__` key trip the inherited setter instead of landing as a key.
export function json_construct(
  val: unknown,
  ...properties: unknown[]
): Record<string | number, unknown> {
  let entries: Map<string, unknown>;
  if (typeof val === "object" && !Array.isArray(val) && val !== null) {
    entries = new Map(Object.entries(val));
  } else if (!val) {
    entries = new Map();
  } else {
    throw new UserInputError(
      "Initial argument must be a JSON object or undefined",
    );
  }

  let currentKey;
  for (const [idx, property] of properties.entries()) {
    if (idx % 2 === 0) {
      // this is the property key
      if (typeof property !== "string" && typeof property !== "number") {
        throw new UserInputError("Property key must be a string or number");
      }
      currentKey = String(property); // a Map distinguishes 2 from "2"
    } else {
      // this is the property value
      entries.set(currentKey, property);
    }
  }
  return Object.fromEntries(entries);
}

function includes(val: string, substring: string): boolean {
  return val.includes(substring);
}

function includes_validate(val: string, substring: string): string {
  if (!val.includes(substring))
    throw new ValidationError(`Input ${val} did not contain ${substring}`);
  return val;
}

/**
 * Parse value to date if it's a string.
 *
 * @param value value to be parsed
 * @param format this accepts the similar format from oob of Liquid https://strftime.org/
 * @returns instance of Date representing the parse, returns the original value if unable to parse.
 */
function parse_date(value: unknown, format: string) {
  if (typeof value !== "string") return value;

  const parse = d3.utcParse(format);
  const result = parse(value);
  if (result instanceof Date) return result;

  // return original value if not parseable.
  return value;
}

export function to_date(val: unknown, format?: string): unknown {
  if (format) return parse_date(val, format);

  return new Date(val as any);
}

export function to_unix(val: Date | string): number {
  let result: number;
  if (typeof val === "string") {
    result = new Date(val).getTime();
  } else {
    result = val.getTime();
  }
  if (isNaN(result)) {
    throw new ValidationError(`Input ${val} is not a valid date`);
  }
  return result;
}

function b64_encode(val: string): string {
  return btoa(val);
}

function b64_decode(val: string): string {
  return atob(val);
}

function regex_test(val: string, pattern: string, flags?: string): string {
  const regEx = RegExp(pattern, flags);
  if (!regEx.test(val))
    throw new ValidationError(`Input ${val} did not contain a RegEx match`);
  return val;
}

function regex_replace(
  val: string,
  pattern: string,
  replacement: string,
  flags?: string,
): string {
  const regEx = RegExp(pattern, flags);
  return val.replace(regEx, replacement);
}

function null_if_empty(val: string): string | null {
  return val === "" ? null : val;
}

function validate_email(val: string) {
  const regEx =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!regEx.test(val))
    throw new ValidationError(
      `Input ${val} is not a RFC 2822 compliant email address`,
    );
  return val;
}

function cast(val: string, toType: string): string | number | boolean {
  switch (toType.toLowerCase()) {
    case "string":
      return String(val);
    case "number":
      return Number(val);
    case "boolean":
      return typeof val === "string" && val.toLowerCase() === "false"
        ? false
        : !!val;
    default:
      throw new UserInputError(
        `Type ${toType} is not supported for casting. Supported types are: "string", "number", "boolean"`,
      );
  }
}

function push(currArray: unknown[], newValue: unknown): unknown[] {
  if (
    currArray !== null &&
    currArray !== undefined &&
    !Array.isArray(currArray)
  )
    throw new Error(
      `Cannot push item ${newValue} to non-array entity ${currArray}`,
    );
  return [...(currArray || []), newValue];
}

function remove_empty(currArray: unknown[]): unknown[] {
  return (currArray || []).filter((item) => !!item);
}

// `typeof Object.prototype === "object"`, so deleting in place can strip a member
// off a shared builtin.
export function remove_key(
  value: Record<string, unknown>,
  keyToRemove: string,
): Record<string, unknown> {
  if (typeof value !== "object") {
    throw new ValidationError(`Input ${value} is not an object.`);
  }
  return Object.fromEntries(
    Object.entries(value).filter(([key]) => key !== keyToRemove),
  );
}

// Array.isArray(Array.prototype) is true, so aliasing `val` can hand us the
// prototype every array inherits from.
export function array_construct(val: unknown, ...items: unknown[]) {
  let arr: unknown[];
  if (val && Array.isArray(val)) {
    arr = [...val];
  } else if (!val) {
    arr = [];
  } else {
    throw new UserInputError("Initial argument must be an array or undefined");
  }
  arr.push(...items.filter((item) => item != undefined));
  return arr;
}

// The common filters keyed by their EXPLICIT, minification-safe filter name.
//
// Frontend Liquid engines MUST register via this map — never via
// `engine.registerFilter(fn.name, fn)`. The app ships a MINIFIED browser bundle
// in which `Function.name` is mangled, so `fn.name` is garbage and every filter
// would register under the wrong name; a template referencing one by its real
// name (`cast`, `sha256`, …) then fails with "undefined filter" in a strict
// engine (the Liquid migration tool surfaces this; `strictFilters: false`
// previews silently no-op it). Object-literal keys are string literals, so they
// survive minification. The backend runs unminified on Node, where `fn.name` is
// intact, so its `commonFunctions`-based registration is unaffected.
//
// Keep this map in sync with `commonFunctions` below — `functions.test.ts`
// asserts the keys match the functions' names and order.
export const commonFiltersByName = {
  sha256,
  sha512,
  md5,
  parse,
  json_construct,
  includes,
  includes_validate,
  to_date,
  to_unix,
  b64_encode,
  b64_decode,
  regex_test,
  regex_replace,
  null_if_empty,
  validate_email,
  cast,
  push,
  remove_empty,
  remove_key,
  array_construct,
};

// Mirrors `commonFunctions` in the SDK source — keep the order and membership in
// sync; the parity test asserts both arrays expose the same filter names.
export const commonFunctions = [
  sha256,
  sha512,
  md5,
  parse,
  json_construct,
  includes,
  includes_validate,
  to_date,
  to_unix,
  b64_encode,
  b64_decode,
  regex_test,
  regex_replace,
  null_if_empty,
  validate_email,
  cast,
  push,
  remove_empty,
  remove_key,
  array_construct,
];
